import type { ListParams } from '@beonit/adaptive-raindrop';

/**
 * Turns the parameters the design system's table publishes into a query string.
 *
 * The table is the only thing that produces `ListParams`, and the backend is the
 * only thing that consumes the query string, so this is the single place the two
 * meet. There is one of these and not one per gateway, because the day a filter is
 * serialised differently in two screens is the day one of them silently stops
 * filtering.
 *
 * Two rules, both of them the backend's:
 *
 * - A list of values travels **comma-separated**, the way `status=SUCCEEDED,FAILED`
 *   already did before any of this existed. Not repeated keys: the validation pipe
 *   reads a repeated key as an array of strings and the DTOs declare strings.
 * - An empty value is **left out**, never sent empty. The pipe rejects anything it
 *   does not declare and treats what it does declare as given, so `search=` asks
 *   for rows whose name is the empty string.
 */
export function listQuery(params: ListParams, extra: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();

  const put = (key: string, value: unknown): void => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(','));
      return;
    }

    const text = String(value).trim();
    if (text) search.set(key, text);
  };

  put('page', params.page);
  put('pageSize', params.pageSize);
  put('search', params.search);
  put('orderBy', params.orderBy);
  put('orderDir', params.orderDir);

  // Column filters carry the key each column declared in its `filter.param`, which
  // has to be a field the endpoint's DTO declares or the answer is a 422.
  for (const [key, value] of Object.entries(params.filters ?? {})) put(key, value);

  // Whatever the screen adds on top of the table: a fixed group, a source id.
  for (const [key, value] of Object.entries(extra)) put(key, value);

  const query = search.toString();

  return query ? `?${query}` : '';
}
